import FastNoiseLite from "fastnoise-lite-typed";
import { BuildingSettings, EffectSettings, GridSettingsType, Layer, LayerSettings, LayerType, RoadSettings, ZoneSettings } from "../models/layer.model";
import { Patterns } from "../models/patterns.svg.model";
import { EffectsType } from "../models/ui-effects.model";
import { generateUUID } from "../utils/store.utils";
import { Option } from "../models/properties.model";
import { isArray, merge, mergeWith } from "lodash";
import { StringEncodings } from "../../services/image-draw.model";
import { generateBuildingOptions, generateRoadOptions } from "../../services/image-data.utils";
import { GroupRenderData } from "../../services/firebase.model";
import { arrayRemove, arrayUnion, deleteField } from "@angular/fire/firestore";

export const buildNewLayer = (layerType: LayerType, stringEncodings?: StringEncodings): Layer => {
  switch (layerType) {
    case LayerType.Contour: {
      return buildContourLayer()
    }
    case LayerType.Buildings: {
      return buildBuildingsLayer(stringEncodings)
    }
    case LayerType.Water: {
      return buildWaterLayer()
    }
    case LayerType.Overlay: {
      return buildColorOverlayLayer()
    }
    case LayerType.POIGlow: {
      return buildPOIGlowLayer()
    }
    case LayerType.MaterialOverlay: {
      return buildMaterialOverlayLayer(stringEncodings)
    }
    case LayerType.HeightGlow: {
      return buildHeightGlowLayer()
    }
    case LayerType.Roads: {
      return buildRoadsLayer(stringEncodings)
    }
    case LayerType.GridLines: {
      return buildGridLinesLayer()
    }
    case LayerType.Zones: {
      return buildZonesLayer(stringEncodings)
    }
    case LayerType.Base: {
      return buildBaseLayer()
    }
  }
  throw new Error('Invalid layer type ' + layerType)
}

const getLayerSettingsKey = (layer: LayerType): keyof Layer | null => {
  switch(layer) {
    case LayerType.GridLines: {
      return 'gridLinesSettings'
    }
    case LayerType.Contour: {
      return 'contourSettings'
    }
    case LayerType.Buildings: {
      return 'buildingSettings'
    }
    case LayerType.Water: {
      return 'waterSettings'
    }
    case LayerType.Overlay: {
      return 'colorOverlaySettings'
    }
    case LayerType.POIGlow: {
      return 'poiGlowSettings'
    }
    case LayerType.MaterialOverlay: {
      return 'materialOverlaySettings'
    }
    case LayerType.HeightGlow: {
      return 'heightGlowSettings'
    }
    case LayerType.Roads: {
      return 'roadSettings'
    }
    case LayerType.Zones: {
      return 'zoneSettings'
    }
    case LayerType.Base: {
      return null
    }
  }
}

export const migrateOldLayerToModern = (layer: Layer): Layer => {
  const modernLayer = JSON.parse(JSON.stringify(buildNewLayer(layer.layerType))) as Layer

  const mergeStrategy = (a: any, b: any) => {
    if (isArray(b)) {
      return b
    }
    return undefined
  }

  const outputLayer = mergeWith(modernLayer, layer, mergeStrategy)
  return outputLayer
}

const generateSharedSettings = (): LayerSettings => ({
  blendingMode: {label: 'Normal', value: 'source-over'},
  opacity: 1,
  visible: true,
  showEffects: false,
  selectionBlur: {
    blurSize: .01,
    blurSpread: .01
  }
})


export const defaultEffectSettings: EffectSettings = {
  outlineSetttings: {
    outlineColor: '#FFFFFF',
    outlineInnerOutline: true,
    outlineWidth: .5,
    outlineOpacity: 1,
  },
  blurSettings: {
    blurStrength: 1
  },
  dropShadowSettings: {
    dropShadowColor: '#000000',
    dropShadowOffsetX: 1,
    dropShadowOffsetY: 1,
    dropShadowLength: 5
  },
  textureSettings: {
    textureColor: '#FFFFFF',
    textureSelector: {label: 'Hexagon', value: Patterns.Hexagon},
    textureOpacity: 1,
    textureScale: 1,
    textureHideUnderlyingLayer: false
  },
  sepiaSettings: {
    sepiaStrength: 1
  },
  noiseSettings: {
    noiseType: {label: 'Perlin', value: FastNoiseLite.NoiseType.Perlin},
    noiseBlendingMode: {label: 'Normal', value: 'source-over'},
    noiseHideUnderlyingLayer: false,
    noiseFrequency: .01,
    noiseInvertMask: false,
    noiseColorGradient: [
      {color: '#000000', position: 0},
      {color: '#FFFFFF', position: 1}
    ],
    noiseOpacity: 1,
    noiseSeed: 0,
    noiseFractileGain: 0,
    noiseFractileOctives: 0,
    noiseFractileType: {label: 'None', value: FastNoiseLite.FractalType.None},
    noiseFractileLacunarity: 0
  }
}

const buildZonesLayer = (encodings?: StringEncodings): Layer => {
  let zoneGroup: Option[] = []
  const first = encodings?.buildings.at(0)
  if (first) {
    zoneGroup = [
      {label: first, value: 1}
    ]
  }
  const zoneSettings: ZoneSettings = {
    zoneGroup,
    zoneDetail: .5,
    zoneOffset: .1,
    zoneFillEnabled: true,
    zoneFillColor: "#FFFFFF",
    zoneFillOpacity: 1,
    zoneStrokeEnabled: false,
    zoneStrokeWidth: .1 ,
    zoneStrokeColor: "#FFFFFF",
    zoneStrokeOpacity: 1,
    zoneStrokeDashed: false,
    zoneStrokeDashLength: .5,
  }
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Zones Layer",
    layerType: LayerType.Zones,
    effects: [],
    sharedSettings: generateSharedSettings(),
    zoneSettings: zoneSettings,
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildBaseLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Base Layer",
    layerType: LayerType.Base,
    effects: [],
    sharedSettings: generateSharedSettings(),
    effectSettings: {...defaultEffectSettings},
    baseLayerSettings: {
      emptyPixelColor: "#000000",
      emptyPixelOpacity: 1
    }
  }
  return newLayer
}

const buildRoadsLayer = (encodings?: StringEncodings): Layer => {
  let roadGroup: Option[] = []
  const first = encodings?.roads.at(0)
  if (first) {
    roadGroup = [
      {label: first, value: 1}
    ]
  }
  const roadSettings: RoadSettings = {
    roadGroup,
    roadColor: '#FFFFFF',
    roadColorOpacity: 1,
  }
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Road Layer",
    layerType: LayerType.Roads,
    effects: [],
    sharedSettings: generateSharedSettings(),
    roadSettings: roadSettings,
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildBuildingsLayer = (encodings?: StringEncodings): Layer => {
  let buildingGroup: Option[] = []
  const first = encodings?.buildings.at(0)
  if (first) {
    buildingGroup = [
      {label: first, value: 1}
    ]
  }
  const buildingSettings: BuildingSettings = {
    buildingGroup,
    buildingColor: '#FFFFFF',
    buildingColorOpacity: 1,
    buildingWeatheredRoofVariation: 1,
    buildingWeatheredRoof: false
  }
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Building Layer",
    layerType: LayerType.Buildings,
    effects: [],
    sharedSettings: generateSharedSettings(),
    buildingSettings: buildingSettings,
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildColorOverlayLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Color Overlay",
    layerType: LayerType.Overlay,
    effects: [],
    sharedSettings: {
      ...generateSharedSettings(),
      blendingMode: {label: 'Overlay', value: 'overlay'},
    },
    colorOverlaySettings:{
      color: '#FFFFFF',
    },
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildContourLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Contour Layer",
    layerType: LayerType.Contour,
    effects: [],
    sharedSettings: generateSharedSettings(),
    contourSettings: {
      contourTotalLines: 5,
      contourVisibleRange: [0,255],
      contourLineWidth: 2,
      contourResolution: 1,
      contourLineColor: [{color: '#FFFFFF', position: 1}]
    },
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildWaterLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Water",
    layerType: LayerType.Water,
    effects: [],
    sharedSettings: generateSharedSettings(),
    waterSettings: {
      waterColor: [
        {color: '#94d3f8', position: 0},
        {color: '#3177a0', position: 1}
      ],
      waterOpaqueDepth: .6,
      waterMinOpacity: .6,
    },
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildPOIGlowLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Point of Interest Glow",
    layerType: LayerType.POIGlow,
    effects: [
      {
        id: generateUUID(),
        name: "Blur",
        effectType: EffectsType.Blur,
      }
    ],
    sharedSettings: {
      ...generateSharedSettings(),
      blendingMode: {label: 'Multiply', value: 'multiply'},
    },
    effectSettings: {
      ...defaultEffectSettings,
      blurSettings: {
        ...defaultEffectSettings.blurSettings,
        blurStrength: 7
      }
    },
    poiGlowSettings: {
      poiGlowColor: [{color: '#000000', position: 1}, {color: '#FFFFFF', position: 0}],
      poiGlowWidth: .5,
      poiShadowOpacity: 1,
      poiGlowStrength: .8
    }
  }
  return newLayer
}

const buildHeightGlowLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Height Glow",
    layerType: LayerType.HeightGlow,
    effects: [
      {
        id: generateUUID(),
        name: "Blur",
        effectType: EffectsType.Blur,
      }
    ],
    sharedSettings: {
      ...generateSharedSettings(),
      blendingMode: {label: 'Multiply', value: 'multiply'},
    },
    heightGlowSettings: {
      heightGlowColor: [
        {color: '#000000', position: 0},
        {color: '#FFFFFF', position: 1}
      ],
      heightGlowRange: [0, 1],
      heightGlowRamp: 1
    },
    effectSettings: {
      ...defaultEffectSettings,
      blurSettings: {
        ...defaultEffectSettings.blurSettings,
        blurStrength: 7
      }
    }
  }
  return newLayer
}

const buildMaterialOverlayLayer = (encodings?: StringEncodings): Layer => {
  let materialOverlaySelector: Option[] = []
  const grassIdx = encodings?.materials.findIndex(x => x === "Grass")
  if (grassIdx) {
    materialOverlaySelector = [
      {label: "Grass", value: grassIdx + 1}
    ]
  }
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Material Overlay",
    layerType: LayerType.MaterialOverlay,
    effects: [],
    sharedSettings: generateSharedSettings(),
    materialOverlaySettings: {
      materialOverlaySelector,
      materialOverlayColor: '#FFFFFF',
      materialOverlayOpacity: 1,
    },
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

const buildGridLinesLayer = (): Layer => {
  const newLayer: Layer = {
    id: generateUUID(),
    name: "Grid Lines",
    layerType: LayerType.GridLines,
    effects: [],
    sharedSettings: generateSharedSettings(),
    gridLinesSettings: {
      color: '#000000',
      style: {label: 'Grid', value: GridSettingsType.Grid},
      opacity: 1,
      density: .5,
      offsetX: 0,
      offsetY: 0,
    },
    effectSettings: {...defaultEffectSettings}
  }
  return newLayer
}

export const cleanGroupedMapping = (layer: Layer, encodings: StringEncodings): boolean => {
  const findOptionOnImageData = (presetOptions: Option[], allOptions: Option[]): Option[] => {
    return presetOptions.filter(option => {
      return !!allOptions.find(x => x.value === option.value && x.label === option.label)
    })
  }
  switch (layer.layerType) {
    case LayerType.Buildings: {
      layer.buildingSettings!.buildingGroup = findOptionOnImageData(layer.buildingSettings!.buildingGroup, generateBuildingOptions(encodings))
      return layer.buildingSettings!.buildingGroup.length === 0
    }
    case LayerType.Roads: {
      layer.roadSettings!.roadGroup = findOptionOnImageData(layer.roadSettings!.roadGroup, generateRoadOptions(encodings))
      return layer.roadSettings!.roadGroup.length === 0
    }
    case LayerType.Zones:{
      layer.zoneSettings!.zoneGroup = findOptionOnImageData(layer.zoneSettings!.zoneGroup, generateBuildingOptions(encodings))
      return layer.zoneSettings!.zoneGroup.length === 0
    }
    default:
      return false
  }
}

type AnyObject = { [key: string]: any };

export function firebaseDiffObjects(
  before: Record<string, any> | undefined,
  after: Record<string, any> | undefined,
  prefix = ""
): Record<string, any> {
  const updates: Record<string, any> = {};

  // Handle keys present in 'after'
  if (after && typeof after === "object") {
    for (const key of Object.keys(after)) {
      const beforeVal = before ? before[key] : undefined;
      const afterVal = after[key];
      const path = prefix ? `${prefix}.${key}` : key;

      if (typeof afterVal === "object" && afterVal !== null) {
        if (Array.isArray(afterVal)) {
          if (beforeVal === undefined) {
            // Always add new arrays, even if empty
            updates[path] = afterVal;
          } else {
            const beforeArr = Array.isArray(beforeVal) ? beforeVal : [];
            const arraysAreDifferent =
              beforeArr.length !== afterVal.length ||
              beforeArr.some((item, index) => {
                const afterItem = afterVal[index];
                if (
                  typeof item === "object" &&
                  typeof afterItem === "object"
                ) {
                  return JSON.stringify(item) !== JSON.stringify(afterItem);
                }
                return item !== afterItem;
              });

            if (arraysAreDifferent) {
              updates[path] = afterVal;
            }
          }
        } else {
          Object.assign(
            updates,
            firebaseDiffObjects(beforeVal || {}, afterVal, path)
          );
        }
      } else if (beforeVal !== afterVal) {
        updates[path] = afterVal;
      }
    }
  }

  // Handle deletions (keys present in 'before' but not in 'after')
  if (before && typeof before === "object") {
    for (const key of Object.keys(before)) {
      if (!after || !Object.prototype.hasOwnProperty.call(after, key)) {
        const path = prefix ? `${prefix}.${key}` : key;
        updates[path] = deleteField();
      }
    }
  }

  return updates;
}
